// AP-033 · Global notifications context — WebSocket + REST + WebAudio + popup.
// Zero-regression: mounts under AuthProvider; if the user is not logged in it
// does nothing.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { useAuth } from "./AuthContext";
import { playSound, isMuted } from "../lib/soundEngine";

const NotificationsContext = createContext(null);

const DEFAULT_PREFS = {
  sound_enabled: true,
  popup_enabled: true,
  volume: 0.6,
  quiet_hours_enabled: false,
  quiet_from: "22:00",
  quiet_to: "07:00",
  muted_categories: [],
  muted_events: [],
};

const IMPORTANT_KINDS = new Set([
  "process_approved", "process_rejected", "process_published",
  "payment_received", "payment_pending",
]);

export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [events, setEvents] = useState({});
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const wsRetryRef = useRef(0);
  const wsReconnectTimer = useRef(null);
  const prefsRef = useRef(DEFAULT_PREFS);
  useEffect(() => { prefsRef.current = prefs; }, [prefs]);

  // ---------- REST helpers ----------
  const loadList = useCallback(async (params = {}) => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 30, ...params } });
      setItems(data.items || []);
      setUnread(data.unread || 0);
      return data;
    } catch { return { items: [], unread: 0 }; }
  }, []);
  const loadCount = useCallback(async () => {
    try { const { data } = await api.get("/notifications/unread-count"); setUnread(data.unread || 0); } catch {}
  }, []);
  const loadPrefs = useCallback(async () => {
    try { const { data } = await api.get("/notifications/prefs"); setPrefs({ ...DEFAULT_PREFS, ...data }); } catch {}
  }, []);
  const loadEvents = useCallback(async () => {
    try { const { data } = await api.get("/notifications/events"); setEvents(data.events || {}); } catch {}
  }, []);
  const updatePrefs = useCallback(async (patch) => {
    const next = { ...prefsRef.current, ...patch };
    setPrefs(next);
    try { const { data } = await api.patch("/notifications/prefs", patch); setPrefs({ ...DEFAULT_PREFS, ...data }); }
    catch { toast.error("Falha ao salvar preferências."); }
  }, []);

  const markRead = useCallback(async (id) => {
    setItems((p) => p.map((n) => n.id === id ? { ...n, read: true } : n));
    setUnread((u) => Math.max(0, u - 1));
    try { await api.post(`/notifications/${id}/read`); } catch {}
  }, []);
  const markAllRead = useCallback(async () => {
    setItems((p) => p.map((n) => ({ ...n, read: true })));
    setUnread(0);
    try { await api.post("/notifications/read-all"); } catch {}
  }, []);
  const removeNotification = useCallback(async (id) => {
    setItems((p) => p.filter((n) => n.id !== id));
    try { await api.delete(`/notifications/${id}`); } catch {}
    loadCount();
  }, [loadCount]);

  // ---------- Popup + sound dispatch ----------
  const dispatchArrival = useCallback((n) => {
    const p = prefsRef.current;
    // AP-033 · Item 5 · Do NOT play a sound for events this same user triggered
    // (e.g. an admin creating a customer hears their OWN action). Popup still
    // shows so the user gets visual confirmation. Item 6/7 already handled by
    // id-dedupe (setItems) + initial loadList not calling dispatchArrival.
    const selfTriggered = n.meta?.actor_id && n.meta.actor_id === user?.id;
    // Sound — respects per-category override.
    if (!selfTriggered && !isMuted(p, n)) {
      const override = (p.sound_overrides || {})[n.category];
      playSound(override || n.sound_key || "generic", p.volume ?? 0.6);
    }
    // Popup
    if (p.popup_enabled) {
      const isImportant = IMPORTANT_KINDS.has(n.type);
      const opts = {
        description: n.body,
        duration: isImportant ? 8000 : 5000,
        action: n.link ? { label: "Abrir", onClick: () => { window.location.assign(n.link); markRead(n.id); } } : undefined,
      };
      if (n.severity === "success") toast.success(n.title, opts);
      else if (n.severity === "error") toast.error(n.title, opts);
      else if (n.severity === "warning") toast.warning(n.title, opts);
      else if (n.severity === "premium") toast.message(n.title, { ...opts, className: "!bg-slate-900 !text-white !border-slate-700" });
      else toast(n.title, opts);
    }
  }, [markRead]);

  // ---------- WebSocket ----------
  const connectWs = useCallback(() => {
    if (!user?.id) return;
    const token = localStorage.getItem("jms_token");
    if (!token) return;
    const wsUrl = API.replace(/^http/, "ws").replace(/\/api\/?$/, "") + `/api/ws/notifications?token=${encodeURIComponent(token)}`;
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => { setConnected(true); wsRetryRef.current = 0; };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "notification" && msg.notification) {
            const n = msg.notification;
            setItems((prev) => {
              if (prev.some((x) => x.id === n.id)) return prev;
              return [n, ...prev].slice(0, 60);
            });
            if (!n.read) setUnread((u) => u + 1);
            dispatchArrival(n);
          }
        } catch { /* ignore parse errors */ }
      };
      ws.onerror = () => { setConnected(false); };
      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;
        // Exponential backoff up to ~30s.
        const delay = Math.min(30000, 1000 * Math.pow(2, wsRetryRef.current++));
        clearTimeout(wsReconnectTimer.current);
        wsReconnectTimer.current = setTimeout(connectWs, delay);
      };
    } catch { setConnected(false); }
  }, [user?.id, dispatchArrival]);

  // ---------- Boot ----------
  useEffect(() => {
    if (!user?.id) {
      setItems([]); setUnread(0); setConnected(false);
      try { wsRef.current?.close(); } catch {}
      clearTimeout(wsReconnectTimer.current);
      return;
    }
    loadList(); loadPrefs(); loadEvents();
    connectWs();
    // Fallback: periodic count refresh (5min) in case WS misses a msg.
    const iv = setInterval(loadCount, 300000);
    return () => {
      clearInterval(iv);
      clearTimeout(wsReconnectTimer.current);
      try { wsRef.current?.close(); } catch {}
    };
  }, [user?.id, connectWs, loadList, loadPrefs, loadEvents, loadCount]);

  const value = useMemo(() => ({
    items, unread, prefs, events, connected,
    loadList, loadCount, updatePrefs, markRead, markAllRead, removeNotification,
  }), [items, unread, prefs, events, connected, loadList, loadCount, updatePrefs, markRead, markAllRead, removeNotification]);

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) return {
    items: [], unread: 0, prefs: DEFAULT_PREFS, events: {}, connected: false,
    loadList: async () => ({}), loadCount: async () => {}, updatePrefs: async () => {},
    markRead: async () => {}, markAllRead: async () => {}, removeNotification: async () => {},
  };
  return ctx;
}
